import { X, Plus, Trash2 } from 'lucide-react';
import { useState, useEffect } from 'react';
import api from '../../api';

export default function AddModuleModal({ handleClose, allTopics }) {
    // Step tracking and data
    const [currentStep, setCurrentStep] = useState(1);
    const [topics, setTopics] = useState(allTopics);
    const [selectedTopic, setSelectedTopic] = useState('');
    const [subTopics, setSubTopics] = useState([]);
    const [selectedSubTopic, setSelectedSubTopic] = useState('');
    const [isTopicLoading, setIsTopicLoading] = useState(false);
    const [isSubtopicLoading, setIsSubtopicLoading] = useState(false);

    // Module data
    const [isLoading, setIsLoading] = useState(false);
    const [moduleItems, setModuleItems] = useState([
        { content: '', type: 'normal' },
    ]);
    const MODULE_TYPES = [
        'normal',
        'programming',
        'realWorld',
        'SchoolSyllabus',
    ];

    // Fetch subtopics when a topic is selected
    const handleTopicSelect = async (topicId) => {
        setSelectedTopic(topicId);
        setIsSubtopicLoading(true);

        try {
            const response = await api.get(
                `/admin/get-all-subtopics?topicId=${topicId}`
            );
            if (response.data.statusCode === 200) {
                setSubTopics(response.data.data);
                setCurrentStep(2); // Move to subtopic selection with module items
            } else {
                console.error('Failed to fetch subtopics');
            }
        } catch (error) {
            console.error('Error fetching subtopics:', error);
        } finally {
            setIsSubtopicLoading(false);
        }
    };

    // Handle subtopic selection
    const handleSubtopicSelect = (subtopicId) => {
        setSelectedSubTopic(subtopicId);
    };

    // Module item management functions
    const handleAddRow = () => {
        setModuleItems([...moduleItems, { content: '', type: 'normal' }]);
    };

    const handleDeleteRow = (indexToDelete) => {
        if (moduleItems.length <= 1) return;
        const updatedItems = moduleItems.filter(
            (_, index) => index !== indexToDelete
        );
        setModuleItems(updatedItems);
    };

    const handleUpdateRow = (index, field, value) => {
        const updatedItems = [...moduleItems];
        updatedItems[index][field] = value;
        setModuleItems(updatedItems);
    };

    // Save module
    const handleSave = async () => {
        if (!selectedSubTopic || moduleItems.some((item) => !item.content)) {
            return; // Don't save incomplete data
        }

        setIsLoading(true);
        try {
            // Format payload according to required structure
            const modules = moduleItems.map((item) => item.content);
            const types = moduleItems.map((item) => item.type);

            const response = await api.post('/admin/add-modules', {
                subTopicId: selectedSubTopic,
                modules: modules,
                type: types,
            });

            if (response.data.statusCode === 200) {
                handleClose({ modalName: 'module' });
            } else {
                console.error('Failed to add module:', response.data.message);
            }
        } catch (error) {
            console.error('Error adding module:', error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="modal modal-open">
            <div className="modal-box relative max-w-2xl">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-bold flex items-center gap-2">
                        {currentStep === 1 && 'Select Topic'}
                        {currentStep === 2 && 'Add Module Items'}
                    </h2>
                    <button
                        onClick={() => handleClose({ modalName: '' })}
                        className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {/* Step 1: Topic Selection */}
                {currentStep === 1 && (
                    <div className="space-y-6">
                        {isTopicLoading ? (
                            <div className="flex justify-center items-center py-8">
                                <div className="flex flex-col items-center">
                                    <span className="loading loading-spinner loading-lg text-primary"></span>
                                    <p className="mt-4 text-base-content/70">
                                        Loading topics...
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text font-medium">
                                            Select Topic
                                        </span>
                                    </label>
                                    <select
                                        className="select select-bordered w-full"
                                        value={selectedTopic}
                                        onChange={(e) =>
                                            handleTopicSelect(e.target.value)
                                        }
                                    >
                                        <option disabled value="">
                                            Select a Topic
                                        </option>
                                        {topics.map((topic) => (
                                            <option
                                                key={topic._id}
                                                value={topic._id}
                                            >
                                                {topic.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </>
                        )}
                    </div>
                )}

                {/* Step 2: SubTopic Selection and Module Items */}
                {currentStep === 2 && (
                    <div className="space-y-6">
                        {isSubtopicLoading ? (
                            <div className="flex justify-center items-center py-8">
                                <div className="flex flex-col items-center">
                                    <span className="loading loading-spinner loading-lg text-primary"></span>
                                    <p className="mt-4 text-base-content/70">
                                        Loading subtopics...
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="form-control w-full mb-6">
                                    <label className="label">
                                        <span className="label-text font-medium">
                                            Select SubTopic
                                        </span>
                                    </label>
                                    <select
                                        className="select select-bordered w-full"
                                        value={selectedSubTopic}
                                        onChange={(e) =>
                                            handleSubtopicSelect(e.target.value)
                                        }
                                    >
                                        <option disabled value="">
                                            Select a SubTopic
                                        </option>
                                        {subTopics.map((subtopic) => (
                                            <option
                                                key={subtopic._id}
                                                value={subtopic._id}
                                            >
                                                {subtopic.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Module Items Table */}
                                <div>
                                    <div className="flex justify-between items-center mb-2">
                                        <label className="label-text font-medium">
                                            Module Items
                                        </label>
                                        <button
                                            className="btn btn-sm btn-outline btn-primary gap-1"
                                            onClick={handleAddRow}
                                        >
                                            <Plus size={16} />
                                            Add Row
                                        </button>
                                    </div>

                                    <div className="overflow-x-auto max-h-64 overflow-y-auto border rounded-lg">
                                        <table className="table table-compact w-full">
                                            <thead>
                                                <tr>
                                                    <th>Content</th>
                                                    <th>Type</th>
                                                    <th className="w-20">
                                                        Action
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {moduleItems.map(
                                                    (item, index) => (
                                                        <tr
                                                            key={index}
                                                            className="hover"
                                                        >
                                                            <td>
                                                                <input
                                                                    type="text"
                                                                    className="input input-bordered w-full input-sm"
                                                                    placeholder="Enter content"
                                                                    value={
                                                                        item.content
                                                                    }
                                                                    onChange={(
                                                                        e
                                                                    ) =>
                                                                        handleUpdateRow(
                                                                            index,
                                                                            'content',
                                                                            e
                                                                                .target
                                                                                .value
                                                                        )
                                                                    }
                                                                />
                                                            </td>
                                                            <td>
                                                                <select
                                                                    className="select select-bordered w-full select-sm"
                                                                    value={
                                                                        item.type
                                                                    }
                                                                    onChange={(
                                                                        e
                                                                    ) =>
                                                                        handleUpdateRow(
                                                                            index,
                                                                            'type',
                                                                            e
                                                                                .target
                                                                                .value
                                                                        )
                                                                    }
                                                                >
                                                                    {MODULE_TYPES.map(
                                                                        (
                                                                            type
                                                                        ) => (
                                                                            <option
                                                                                key={
                                                                                    type
                                                                                }
                                                                                value={
                                                                                    type
                                                                                }
                                                                            >
                                                                                {
                                                                                    type
                                                                                }
                                                                            </option>
                                                                        )
                                                                    )}
                                                                </select>
                                                            </td>
                                                            <td className="text-center">
                                                                <button
                                                                    className="btn btn-square btn-sm btn-error btn-outline"
                                                                    onClick={() =>
                                                                        handleDeleteRow(
                                                                            index
                                                                        )
                                                                    }
                                                                    disabled={
                                                                        moduleItems.length <=
                                                                        1
                                                                    }
                                                                    title="Delete row"
                                                                >
                                                                    <Trash2
                                                                        size={
                                                                            16
                                                                        }
                                                                    />
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    )
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                {/* Actions */}
                                <div className="flex justify-between">
                                    <button
                                        className="btn btn-outline btn-secondary"
                                        onClick={() => {
                                            setCurrentStep(1);
                                            setSelectedTopic('');
                                            setSelectedSubTopic('');
                                        }}
                                        disabled={isLoading}
                                    >
                                        Back
                                    </button>
                                    <button
                                        className="btn btn-primary"
                                        onClick={handleSave}
                                        disabled={
                                            !selectedSubTopic ||
                                            moduleItems.some(
                                                (item) => !item.content
                                            ) ||
                                            isLoading
                                        }
                                    >
                                        {isLoading ? (
                                            <span className="loading loading-spinner loading-sm"></span>
                                        ) : (
                                            'Save Module'
                                        )}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                )}

                {/* Cancel button always visible */}
                <div className="modal-action">
                    <button
                        className="btn btn-ghost"
                        onClick={() => handleClose({ modalName: '' })}
                        disabled={isLoading}
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
}
